(ns practitest.frontend.forms3.issue
  (:require
    [clojure.string :as str]
    [practitest.frontend.common-entities.events :as common-events]
    [practitest.frontend.common-entities.metadata :as metadata]
    [practitest.frontend.common.bugtrackers :as bugtrackers]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.format-utils :as fu]
    [practitest.frontend.common.i18n :refer [format-translation get-translation] :as i18n]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.common.permissions :as permissions]
    [practitest.frontend.common.re-frame :refer [partial-dispatch]]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.components2.traceability :as traceability]
    [practitest.frontend.forms3.entity-form :as f3.entity-form]
    [practitest.frontend.forms3.entity-form.forms :as f3.forms]
    [practitest.frontend.forms3.entity-form.utils :refer [track!]]
    [practitest.frontend.issue.bugtracker :as bugtracker :refer [bugtracker-type]]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.kanban :as kanban]
    [practitest.frontend.similar-items :as similar-items]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]))

(def current-entity :issue)
(def metadata (get metadata/entities-metadata current-entity))
(def edit-permission (:edit-permissions metadata))
(def admin-permission (:delete-permissions metadata))
(def ^:private issue-form-id (:form-id metadata))

(defn show-bugtracker-menu-item? [issue]
  (let [bugtracker (bugtracker-type @(re-frame/subscribe [::subs/current-project]))]
    (and (nil? (:external_id issue))
         (or
           (bugtrackers/jira? bugtracker)
           (bugtrackers/clickup? bugtracker))
         (permissions/has-permission? "issues_editor"))))

(defn bugtracker-logo [bugtracker]
  (->
    (str/lower-case bugtracker)
    keyword
    bugtracker/icons-map-by-bugtracker))

(defn issue-menu []
  (let [issue (:current @(re-frame/subscribe [::subs/entity current-entity]))
        {:keys [urls]} metadata
        issue-id (:id issue)
        path-params {:id issue-id}
        print-issue-uri (broutes/get-url-view (:print urls) path-params {"ids" issue-id})
        has-admin-permissions (permissions/has-permission? admin-permission)
        current-project (re-frame/subscribe [::subs/current-project])
        delete-permission? (and has-admin-permissions
                                (:enable_delete_issues @current-project))]
    [{:text     (get-translation "generic.button.delete" "Delete")
      :test-id  "issue-form-menu-delete"
      :disabled? (not delete-permission?)
      :on-click (fn [e]
                  (when delete-permission?
                    (dom/prevent-default e)
                    (when (js/confirm (get-translation "entity.confirm.action-delete" "Are you sure? This Operation cannot be undone."))
                      (track! "Delete entity" current-entity)
                      (re-frame/dispatch [::common-events/delete-current-entity issue-form-id]))))
      :icon     [icons/mux-trash]}
     (when (show-bugtracker-menu-item? issue)
       (let [bugtracker (bugtracker-type @current-project)
             entity-mapping-enabled? (:jira_entity_mapping_enabled @current-project)
             sync-disabled? (and (bugtrackers/jira? bugtracker)
                                 entity-mapping-enabled?
                                 (not (:issues_sync_enabled @current-project true)))
             {:keys [name description id]} issue]
         {:text (format-translation
                  (get-translation "issue.bugtracker.create" "Create Issue in %{bugtracker}")
                  {:bugtracker (bugtrackers/get-label bugtracker)})
          :test-id "issue-form-menu-create-bugtracker"
          :disabled? sync-disabled?
          :tooltip   (when sync-disabled?
                       (get-translation "entity.dropdown.sync_disabled_tooltip"
                                        "This option has been disabled by your administrator's settings."))
          :on-click #(re-frame/dispatch [::bugtracker/show-bugtracker-modal! :create {:issue-name name
                                                                                      :issue-description description
                                                                                      :params {:entity_id id}}])
          :icon     [(bugtracker-logo bugtracker)]}))

     {:text        (get-translation "entity.button.print" "Print")
      :test-id     "issue-form-menu-print"
      :icon        [icons/mux-printer]
      :mixpanel    ["Form Actions" {"Entity" "Issue" "Action" "Print entity"}]
      :new-window? true
      :href        print-issue-uri}]))

(defn build-issue-tabs
  [edit?]
  [(f3.entity-form/general-entity-tab)

   (traceability/build-traceability-tab)

   (when edit?
     (f3.entity-form/history-tab))])

(defn from-testset-footer-buttons
  [form-props save-form! mixpanel-attrs]
  [:div.m-button-group
   [buttons/button
    {:text     (get-translation "generic.button.cancel" "Cancel")
     :on-click (fn [_]
                 (mixpanel/track "Cancel Save Entity" mixpanel-attrs)
                 ;; TODO: back + scroll to step ??? (not exactly back)
                 (js/history.back))}]
   [buttons/button
    {:text     (get-translation "generic.button.create" "Create")
     :test-id "form-save-btn"
     :on-click (fn [_]
                 (mixpanel/track "Create Entity" mixpanel-attrs)
                 (save-form! form-props {:close? false}))}]
   [buttons/button
    {:text     (get-translation "issue.button.and-continue-testing" "Create & continue testing")
     :primary? true
     :on-click (fn [_]
                 (mixpanel/track "Create Entity and continue testing" mixpanel-attrs)
                 (save-form! form-props {:continue-testing? true}))}]])

(re-frame/reg-sub
  ::fail-and-issue-details
  (fn [db]
    (get-in db [:form-states2 issue-form-id :fail-and-issue :details])))

(re-frame/reg-sub
  ::issue-from-et-annotation-details
  (fn [db]
    (get-in db [:form-states2 issue-form-id :from-et-annotation :details])))

(defn from-run-breadcrumbs [{:keys [from_step_id]}]
  (let [fail-and-issue @(re-frame/subscribe [::fail-and-issue-details])
        issue-from-et-annotation @(re-frame/subscribe [::issue-from-et-annotation-details])
        issue-details (if from_step_id fail-and-issue issue-from-et-annotation)]
    (if issue-details
      (let [{:keys [steprun testset instance et]} issue-details
            run (:run steprun)
            test (:test run)
            breadcrumbs [{:href  (broutes/get-url-view "suites#edit" {:id (:id testset)})
                          :label (str (f3.forms/entity->display-name :testset) " #" (:display_id testset)
                                      ": " (:name testset))}
                         (when instance
                           {:href  (broutes/get-url-view "instances#edit" {:id (:id instance)})
                            :label (str (f3.forms/entity->display-name :instance)
                                        " of " (f3.forms/entity->display-name :test) " #" (:display_id test)
                                        ": " (:name run))})
                         (when steprun
                           {:href  (broutes/get-url-view "qtest_runs#edit" {:id (:id run)})
                            :label (str (fu/formatted-date-time (:run_date run)) ": " (:status run))})
                         (when et
                           {:href  (broutes/get-url-view "et#edit" {:s_id (:id testset)
                                                                    :id (:id et)})
                            :label (str (f3.forms/entity->display-name :et) " #" (:id et)
                                        ": " (:name et))})]]
        (remove nil? breadcrumbs))
      [])))

(defn- bugtracker-fields [base-entity all-fields]
  (let [linked-issue? (boolean (:external_id base-entity))]
    (if linked-issue?
      (filter
        #(true? (get-in % [:bugtracker :issue]))
        (bugtracker/bugtracker-fields all-fields))
      [])))

(defn- issue-form-layout
  [entity-fields external-id]
  (let [kanban-enabled? @(re-frame/subscribe [::subs/kanban-enabled?])
        nil-or-unavailable? (fn [field]
                              (or (nil? field)
                                  (not (contains? entity-fields field))))
        initial-layout {:title {:fields [{:field :name}]}
                        (:description f3.entity-form/sections-keys) {:fields [{:field :description}]}}
        existing-fields (set (f3.forms/flatten-layout initial-layout))
        to-remove? (fn [field]
                     (or (nil-or-unavailable? field)
                         (contains? existing-fields field)))

        filtered-entity-fields (cond-> (f3.entity-form/filter-entity-fields entity-fields to-remove?)
                                 kanban-enabled? (assoc :kanban_lane kanban/kanban-field))
        filtered-entity-fields-flat (vec (keys filtered-entity-fields))
        entity-sections @(re-frame/subscribe [::subs/entity-sections current-entity])

        update-main-sections (if (some? entity-sections)
                               #(f3.entity-form/update-layout % entity-sections filtered-entity-fields)
                               #(f3.entity-form/update-layout % (:general-info f3.entity-form/sections-keys) filtered-entity-fields-flat "general_info" entity-fields))

        layout (-> initial-layout
                   (update-main-sections)
                   (f3.entity-form/handle-integration-fields external-id))]
    layout))

(defmethod f3.forms/build-entity-form-layout issue-form-id
  [_ _ _ entity-fields entity]
  (issue-form-layout entity-fields (:external_id entity)))

;; Additional fields to be sent to server for issues
(defn- issue-form-params-helper
  [from_step_id from_et_id db form-params]
  ;; Extra magical fields for fail & issue / report issue support
  (let [fast-run-params (get-in db [:form-states2 issue-form-id :fast-run])
        result (cond-> form-params
                 ;; Fast run from step run
                 (and (= from_step_id "fast_run") fast-run-params)
                 (-> (assoc-in [current-entity :started_from_fast_run] true)
                     (assoc :ids (:ids fast-run-params))
                     (assoc :set_id (:set-id fast-run-params)))

                 ;; Regular step run
                 (and (some? from_step_id) (not= from_step_id "fast_run"))
                 (assoc-in [current-entity :started_from_step_run_id] from_step_id)

                 (some? from_et_id)
                 (assoc-in [current-entity :started_from_et_annotation_id] from_et_id)

                 (or from_step_id from_et_id)
                 (assoc :back_to_test true))]
    result))

(re-frame/reg-event-fx
  ::save-issue
  (fn [{db :db} [_ from_step_id from_et_id {:keys [new?] :as options}]]
    {:dispatch [::common-events/save-entity issue-form-id current-entity
                (assoc options :additional-params-helper (partial issue-form-params-helper from_step_id from_et_id))]}))

(defn main [new? {:keys [from_step_id from_et_id] :as query-params}]
  (let [current-issue (:current @(re-frame/subscribe [::subs/entity current-entity]))
        external-id (:external_id current-issue)]
    [:<>
     [bugtracker/bugtracker-integration-modal]
     [f3.entity-form/entity-form
      {:new?              new?
       :popup-menu        (issue-menu)
       :buttons-override  (when (and (or from_step_id from_et_id)
                                     new?)
                            from-testset-footer-buttons)
       :on-save           (partial-dispatch [::save-issue from_step_id from_et_id])
       :entity-type       current-entity
       :form-id           issue-form-id
       :breadcrumbs       (if (or from_step_id from_et_id)
                            ;; Need to wait for data to be fetched before rendering breadcrumbs
                            (from-run-breadcrumbs query-params)
                            [{:href  (broutes/get-url-view (get-in metadata [:urls :index]) {:project-id (:id @(re-frame/subscribe [::subs/current-project]))})
                              :label (get-translation "navigation.project-menu.issues" "Issues")}])
       :header-tabs       (build-issue-tabs (not new?))
       :edit-permissions? (permissions/has-permission? edit-permission)
       :customization-permissions? (permissions/has-permission? "customization")
       :additional-fields (when external-id
                            bugtracker/additional-fields-form3)
       :general-form-inner-tabs {:new #{:attachments :similar-items}
                                 :edit #{:attachments :comments :similar-items}}
       :default-active-sub-tab (if new? :similar-items :attachments)}]]))
